import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'th' | 'en' | 'lo';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('th');
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [translations, setTranslations] = useState<Record<string, any>>({
    // Complete default fallback translations
    common: {
      loading: "กำลังโหลด...",
      save: "บันทึก",
      cancel: "ยกเลิก"
    },
    quickActions: {
      transfer: "โอนเงิน",
      scan: "สแกน QR", 
      myQR: "QR ของฉัน"
    },
    navigation: {
      profile: "บัญชี"
    },
    time: {
      time: "เวลา",
      currentTime: "เวลาปัจจุบัน",
      currentDate: "วันที่ปัจจุบัน"
    },
    auth: {
      calendar: "ปฏิทิน"
    },
    bottomNav: {
      home: "หน้าหลัก",
      transfer: "โอน", 
      scanQR: "QR",
      history: "ประวัติ",
      exchange: "แลกเปลี่ยน"
    },
    pinVerification: {
      title: "ยืนยันตัวตน",
      description: "กรุณาใส่ PIN เพื่อเข้าใช้งานบนอุปกรณ์นี้"
    },
    qrPayment: {
      title: "การชำระด้วย QR",
      description: "ชำระด้วย QR Code - สะดวก รวดเร็ว ปลอดภัย",
      currentBalance: "ยอดเงินปัจจุบัน",
      generateQR: "สร้าง QR",
      scanQR: "สแกน QR",
      generateQRTitle: "สร้าง QR Code สำหรับการชำระเงิน",
      scanQRTitle: "สแกน QR Code",
      amount: "จำนวนเงิน (บาท)",
      merchantName: "ชื่อร้าน (ไม่บังคับ)",
      merchantPlaceholder: "ชื่อร้านของคุณ",
      generateButton: "สร้าง QR Code",
      qrReady: "QR Code พร้อมแล้ว",
      expiresIn: "QR Code นี้จะหมดอายุใน",
      expired: "หมดอายุแล้ว",
      download: "ดาวน์โหลด",
      share: "แชร์",
      openCamera: "เปิดกล้องเพื่อสแกน QR",
      merchant: "ร้าน",
      remainingBalance: "ยอดเงินคงเหลือ",
      confirmPayment: "ยืนยันการชำระเงิน",
      payment: "การชำระเงิน",
      to: "ให้",
      scanDescription: "จ่อกล้องไปที่ QR Code เพื่อสแกน"
    }
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('app-language') as Language;
    
    if (savedLanguage && ['th', 'en', 'lo'].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    } else {
      setLanguageState('th');
      localStorage.setItem('app-language', 'th');
    }
  }, []);

  useEffect(() => {
    // Load translations for current language
    loadTranslations(language);
    // Update document language attribute
    document.documentElement.lang = language;
  }, [language]);

  const loadTranslations = async (lang: Language) => {
    setIsLoading(true);
    try {
      const module = await import(`../translations/${lang}.json`);
      setTranslations(module.default);
      setIsInitialLoad(false);
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      if (lang !== 'th') {
        try {
          const fallback = await import('../translations/th.json');
          setTranslations(fallback.default);
          setIsInitialLoad(false);
        } catch (fallbackError) {
          console.error('Failed to load fallback translations:', fallbackError);
          setIsInitialLoad(false);
        }
      } else {
        setIsInitialLoad(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
  };

  const t = (key: string): string => {
    if (!key) return '';
    
    const keys = key.split('.');
    let result: any = translations;
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        const lastKey = keys[keys.length - 1];
        return lastKey.charAt(0).toUpperCase() + lastKey.slice(1);
      }
    }
    
    return typeof result === 'string' ? result : key;
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  // Show loading state only during initial load
  if (isInitialLoad && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};